import type { Symbol } from './instruments';
import { validTick } from './live-data';
import type { Tick } from './live-data';
import { validBidAsk } from './bid-ask';
import type { BidAsk } from './bid-ask';
import { DERIV_SYMBOLS, ENDPOINT } from './deriv-feed.js';
export { DERIV_SYMBOLS } from './deriv-feed.js';

export type StreamSymbol = Symbol | 'USDJPY';
export type QuotePacket = { symbol: StreamSymbol; source: string; priceType: 'quote'; sessionOffsetMinutes: number; ticks: Tick[]; quote: Tick | null; book: BidAsk | null; coverageFrom: number; fetchedAt: string };
// Public, read-only socket. History and live prices use the same QUOTE field.
// Bid/ask are separate display levels; candles continue to use QUOTE.
export function startDerivStream(symbol: StreamSymbol, data: (packet: QuotePacket) => void, failed: () => void, socketFactory = () => new window.WebSocket(ENDPOINT)) {
  const underlying = symbol === 'USDJPY' ? 'frxUSDJPY' : DERIV_SYMBOLS[symbol];
  if (!underlying) return () => {};
  let disposed = false, generation = 0, attempts = 0, socket: WebSocket | null = null;
  let retry: ReturnType<typeof setTimeout>, watchdog: ReturnType<typeof setTimeout>, ping: ReturnType<typeof setInterval>;
  function close() {
    generation++;
    clearTimeout(retry); clearTimeout(watchdog); clearInterval(ping);
    const old = socket; socket = null;
    try { old?.close(); } catch {}
  }
  function connect() {
    close();
    if (disposed || document.hidden) return;
    const current = generation;
    let coverage = 0, lastTime = 0, ready = false;
    const active = () => !disposed && current === generation;
    const fail = () => {
      if (!active()) return;
      close(); failed();
      retry = setTimeout(connect, Math.min(30000, 1000 * 2 ** Math.min(++attempts, 5)));
    };
    // A working ping only proves the socket is open, not that its price
    // subscription still works. Only fresh price data extends this deadline.
    const touch = () => { clearTimeout(watchdog); watchdog = setTimeout(fail, 15000); };
    try { socket = socketFactory(); } catch { fail(); return; }
    const ws = socket;
    watchdog = setTimeout(fail, 6500);
    ws.addEventListener('open', () => {
      if (!active()) return;
      try {
        ws.send(JSON.stringify({ ticks_history: underlying, style: 'ticks', start: Math.floor((Date.now() - 120000) / 1000), count: 1000, end: 'latest', subscribe: 1, req_id: 1 }));
      } catch { fail(); return; }
      ping = setInterval(() => {
        if (active() && ws.readyState === 1) {
          try { ws.send(JSON.stringify({ ping: 1, req_id: 2 })); } catch { fail(); }
        }
      }, 20000);
    });
    ws.addEventListener('message', event => {
      if (!active()) return;
      try {
        const value = JSON.parse(event.data), now = Date.now();
        if (value.error) { fail(); return; }
        if (value.req_id !== 1) return;
        let ticks: Tick[];
        let book: BidAsk | null = null;
        if (value.msg_type === 'history') {
          const h = value.history;
          if (!Array.isArray(h?.times) || !Array.isArray(h.prices) || h.times.length !== h.prices.length) throw new Error('Invalid history');
          ticks = h.times.map((epoch: number, i: number) => ({ time: epoch * 1000, price: h.prices[i] }));
          coverage = Math.max(now - 120000, ticks[0]?.time || now);
          ready = true;
        } else if (value.msg_type === 'tick' && ready) {
          if (value.tick?.symbol !== underlying) throw new Error('Wrong pair');
          ticks = [{ time: value.tick.epoch * 1000, price: value.tick.quote }];
          const bidAsk = { time: value.tick.epoch * 1000, bid: value.tick.bid, ask: value.tick.ask };
          if (validBidAsk(bidAsk, now)) book = bidAsk;
        } else return;
        if (ticks.some((tick, i) => !validTick(tick, now) || (i > 0 && tick.time < ticks[i - 1].time))) throw new Error('Invalid prices');
        ticks = ticks.filter(tick => tick.time >= lastTime);
        if (ticks.length) lastTime = ticks.at(-1)!.time;
        if (ticks.length && now - lastTime < 10000) { attempts = 0; touch(); }
        data({ symbol, source: 'Deriv', priceType: 'quote', sessionOffsetMinutes: 0, ticks, quote: ticks.at(-1) || null, book: ticks.length ? book : null, coverageFrom: coverage, fetchedAt: new Date(now).toISOString() });
      } catch { fail(); }
    });
    ws.addEventListener('error', fail);
    ws.addEventListener('close', fail);
  }
  const visibility = () => { if (document.hidden) close(); else connect(); };
  const restore = (event: PageTransitionEvent) => { if (event.persisted) connect(); };
  document.addEventListener('visibilitychange', visibility);
  window.addEventListener('online', connect);
  window.addEventListener('pageshow', restore);
  connect();
  return () => { disposed = true; close(); document.removeEventListener('visibilitychange', visibility); window.removeEventListener('online', connect); window.removeEventListener('pageshow', restore); };
}
